// Isolated "v2" copy of the redesigned Track Record verification UI.
// Used only by crm-portal and roc-internal-portal. The original component in
// @roc/feature-borrowers is left untouched so other portals keep the old design.
// MobX stores/services are still shared (imported from @roc/feature-borrowers).
import { Box, Grid, Paper, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { makeStyles, Theme, alpha, lighten, useTheme } from '@material-ui/core/styles';
import { useCenteredDialogStyles } from './centeredDialogStyles';
import { TrackRecordFirstStep } from './trackRecordFirstStep';
import { TrackRecordThirdStep } from './trackRecordThirdStep';
import { useEffect, useRef, useState } from 'react';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import BusinessIcon from '@material-ui/icons/Business';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import GetAppIcon from '@material-ui/icons/GetApp';
import EditIcon from '@material-ui/icons/Edit';
import { Button as RocButton, FileUpload, Tabs } from '@roc/ui';
// Reused as-is from the legacy baseball card (read-only import, internal portal only).
import EditBorrowerExperienceModal from 'libs/ui/src/lib/components/borrowerBaseballCard/editBorrowerExperienceModal';
import { CompactFileUploadArea } from '../components/compactFileUploadArea';
// FileUploadItemWithDownload is not re-exported from the @roc/ui barrel, so it's
// imported directly from its source module (same pattern the compact upload areas use).
import { FileUploadItemWithDownload } from 'libs/ui/src/lib/components/fileUploadModalWithDownload/fileUploadModalWithDownload';
import {
  PublicTrackRecordLineItemV2 as PublicTrackRecordLineItem,
  PropertyInformationFormLineItemV2 as PropertyInformationFormLineItem,
} from '../components/borrowerBaseballCardV2';
import { useBorrowerBaseballCardStore } from '@roc/feature-borrowers';
import { useDownloadTrackRecordTemplate } from '../hooks/useDownloadTrackRecordTemplate';
import { TrackRecordProperties } from './trackRecordProperties';
import { TrackRecordEntities } from './trackRecordEntities';
import { TrackRecordLicenses } from './trackRecordLicenses';
// Reused as-is from the legacy baseball card (not exported from the lib index, so
// imported by path like the original card does). Internal-portal only.
import NewBorrowerExperienceData from 'libs/feature-borrower-review/src/components/newBorrowerExperienceData';

const useStyles = makeStyles((theme: Theme) => ({
  footerButton: {
    marginRight: theme.spacing(1),
  },
  addTrackRecordContainer: {
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    padding: 16,
    margin: '0 20px',
  },
  optionButton: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: '16px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#fff',
    height: '100%',
    boxSizing: 'border-box',
    transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    },
  },
  optionButtonActive: {
    position: 'relative',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: '16px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    height: '100%',
    boxSizing: 'border-box',
    transition: 'background-color 0.2s, box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    },
  },
  optionButtonRecommended: {
    position: 'relative',
    border: `1.5px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    padding: '16px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#fff',
    height: '100%',
    boxSizing: 'border-box',
    transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    },
  },
  recommendedChip: {
    position: 'absolute',
    top: -9,
    left: 16,
    backgroundColor: lighten(theme.palette.primary.main, 0.85),
    color: theme.palette.primary.main,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.5,
    padding: '1px 10px',
    borderRadius: 12,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
  templateLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: theme.palette.primary.dark,
    cursor: 'pointer',
    fontSize: 14,
  },
  experienceCard: {
    border: '1px solid lightgrey',
    borderRadius: 10,
    overflow: 'hidden',
  },
  experienceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid lightgrey',
    padding: '12px 16px',
  },
  experienceBody: {
    padding: '0 16px 8px',
  },
}));

export const BorrowerTrackRecordVerification = observer(({
  token,
  isInternal = false,
  isTpoFlow = false,
  store,
  showBanner = true,
  borrowerId = null,
  borrowerOnly = false,
  onDocUploadCallback = () => { },
  onClose,
  onSaveCompleted = null,
}) => {
  const { borrowerTrackRecordVerificationStore } = store;
  const { trackRecordOtherFormStore } = borrowerTrackRecordVerificationStore;
  const classes = useStyles();
  const centeredDialogClasses = useCenteredDialogStyles();
  const theme = useTheme();
  // Both portals provide borrowerBaseballCardStore through the same StoreContext
  // (useRocInternalPortalStore and useBorrowerBaseballCardStore both read it), so a
  // single unconditional hook call works for internal and public portals — and keeps
  // us within the Rules of Hooks. loanId is seeded from the loan store in the effect
  // below; we no longer depend on which hook wrapper is used.
  const { borrowerBaseballCardStore } = useBorrowerBaseballCardStore();
  const downloadTrackRecordTemplate = useDownloadTrackRecordTemplate();
  const [inlineUploadActive, setInlineUploadActive] = useState(false);
  const [inlineEntityActive, setInlineEntityActive] = useState(false);
  const [inlinePropertyActive, setInlinePropertyActive] = useState(false);
  const [inlineTemplateUploads, setInlineTemplateUploads] = useState<FileUpload[]>([]);
  // Bottom "Properties" / "Entities" record tabs (0 = Properties, 1 = Entities).
  const [bottomTab, setBottomTab] = useState(0);
  // Edit Borrower Experience modal (internal portal only).
  const [editExperienceOpen, setEditExperienceOpen] = useState(false);

  // Refs to the inline lookups' original save handlers, so the footer
  // "Save and Finish" button can trigger the pre-existing save flow.
  const entitySaveRef = useRef<null | (() => Promise<void>)>(null);
  const propertySaveRef = useRef<null | (() => Promise<void>)>(null);

  // Fire the new Bedrock agent API (fire-and-forget — result is ignored for now).
  // Invoked on the Search click in the look-up popups with exactly what the user
  // searched: the entity name(s) for entity look-up, the address for property look-up.
  const fireAgentForEntity = (entityNames: string[]) => {
    borrowerTrackRecordVerificationStore.invokeAgentFireAndForget({ entityNames });
  };
  const fireAgentForProperty = (customTransactions: any[]) => {
    borrowerTrackRecordVerificationStore.invokeAgentFireAndForget({ customTransactions });
  };

  useEffect(() => {
    if (!inlineUploadActive || inlineTemplateUploads.length === 0) return;

    const run = async () => {
      const s = borrowerTrackRecordVerificationStore;
      try {
        await s.setTemplateUploads(inlineTemplateUploads);
        // Template upload mode on master is digitalTrackRecord=false. The upload
        // card used to set true, which blocked parseTrackRecordTemplate entirely.
        await s.setDigitalTrackRecord(false);

        const id = s.borrowerInfo?.borrowerId || s.borrowerId;
        if (!id) {
          s.globalStore.notificationStore.showErrorNotification({
            message: 'Borrower information is still loading. Please try again in a moment.',
          });
          return;
        }

        await s.parseTrackRecordTemplate(
          () => {
            s.fetchUploadedTrackRecordFile();
            onDocUploadCallback();
          },
          { forceTemplateParse: true }
        );
      } catch (e) {
        console.error('Track record template upload failed:', e);
        s.globalStore.notificationStore.showErrorNotification({
          message: 'Error occurred while uploading the track record template',
        });
      }
    };

    void run();
  }, [inlineTemplateUploads, inlineUploadActive]);

  useEffect(() => {
    // v2-only default: start with 'No' so the "Add Track Record" section stays
    // hidden during the initial render. fetchBorrowerInfo() below will overwrite
    // this with the borrower's real value once it loads (the shared store coerces
    // a nil previousExperience to 'No' too). Done here instead of changing the
    // shared TrackRecordOtherFormStore default ('Yes'), which other portals rely on.
    trackRecordOtherFormStore.onFieldChange('previousExperience', 'No');
    // v2-only default: also start General Contractor / Broker on 'No' instead of
    // the shared store's nil (which renders as nothing selected). fetchBorrowerInfo()
    // below overwrites these with the borrower's real values once they load.
    trackRecordOtherFormStore.onFieldChange('generalContractor', 'No');
    trackRecordOtherFormStore.onFieldChange('broker', 'No');
    borrowerTrackRecordVerificationStore.setCurrentToken(token);
    borrowerTrackRecordVerificationStore.checkBorrowerId(borrowerId);
    borrowerTrackRecordVerificationStore.fetchBorrowerInfo();
    borrowerTrackRecordVerificationStore.resetActiveStep();
    borrowerTrackRecordVerificationStore.setIsTpoFlow(isTpoFlow);
  }, []);

  // Handle late-arriving borrowerId (e.g. when loan borrowers load async)
  useEffect(() => {
    if (borrowerId && !borrowerTrackRecordVerificationStore.borrowerId) {
      borrowerTrackRecordVerificationStore.checkBorrowerId(borrowerId);
      borrowerTrackRecordVerificationStore.fetchBorrowerInfo();
    }
  }, [borrowerId]);

  // Internal portal only: load the borrower's baseball card so the "Borrower
  // Experience" block has data (the verification store doesn't populate the
  // baseball card store). Keyed on the resolved borrowerId so it runs once it loads.
  useEffect(() => {
    const bId = borrowerTrackRecordVerificationStore?.borrowerInfo?.borrowerId;
    if (isInternal && bId) {
      // Keep currentBorrower in sync so the save handler and the post-save refetch
      // both resolve to the same borrower id (avoids the dual-source mismatch).
      borrowerBaseballCardStore.setCurrentBorrower(bId);
      borrowerBaseballCardStore.fetchBaseballCardData(bId);
      // fetchBaseballCardData only fills baseballCardDetails
      borrowerBaseballCardStore.getTrackRecords(bId, borrowerBaseballCardStore.loanId);
    }
  }, [isInternal, borrowerTrackRecordVerificationStore?.borrowerInfo?.borrowerId]);

  // Once borrowerId resolves, load persisted borrower-level files for the popup.
  useEffect(() => {
    const bId =
      borrowerTrackRecordVerificationStore?.borrowerInfo?.borrowerId
      || borrowerTrackRecordVerificationStore?.borrowerId
      || borrowerId;
    if (!bId) return;

    const loanTodosStore = borrowerOnly ? null : store?.documentStore?.loanTodosStore;
    const loanId = borrowerOnly
      ? null
      : (store?.documentStore?.loanStore?.loanDetails?.loanId
        ?? borrowerBaseballCardStore?.loanId);

    // 1) Quick path: resolve from the already-loaded loan to-do list (taskV2 cache).
    const fromLoanTasks = borrowerTrackRecordVerificationStore.loadLicensesFromLoanTodos(loanTodosStore);
    const resolvedFromTodos = fromLoanTasks
      && (borrowerTrackRecordVerificationStore.gcLicenseTaskId
        || borrowerTrackRecordVerificationStore.brokerLicenseTaskId);

    if (loanId) {
      // Loan context: keep the existing borrower-public read for the Track Record popup file.
      borrowerTrackRecordVerificationStore.fetchUploadedTrackRecordFile(bId);
    }

    if (resolvedFromTodos) {
      borrowerTrackRecordVerificationStore.fetchLicensesByTaskId();
    } else if (loanId) {
      // 2) Resolve straight from taskV2 by loan + borrower. Avoids the borrower-public getTasks/createTasks that 401
      Promise.resolve(
        borrowerTrackRecordVerificationStore.resolveLicensesByLoanAndBorrower(loanId, bId),
      ).then(() => borrowerTrackRecordVerificationStore.fetchLicensesByTaskId());
    } else {
      // 3) Borrower-only flow (no loanId): resolve the borrower-level master task(s) —
      //    licenses AND the Track Record file — directly from taskV2 (type1=BORROWER_ID).
      //    Does NOT touch the borrower-public getTasks endpoint (rejects objectType=BORROWER).
      Promise.resolve(
        borrowerTrackRecordVerificationStore.resolveLicensesByBorrower(bId),
      ).then(() => borrowerTrackRecordVerificationStore.fetchLicensesByTaskId());
    }
  }, [
    borrowerTrackRecordVerificationStore?.borrowerInfo?.borrowerId,
    borrowerTrackRecordVerificationStore?.borrowerId,
    borrowerId,
    borrowerOnly,
  ]);

  // v2-only: once the borrower has any properties or entities
  useEffect(() => {
    const hasTrackRecord =
      (borrowerTrackRecordVerificationStore.properties?.length || 0) > 0 ||
      (borrowerTrackRecordVerificationStore.entities?.length || 0) > 0;
    if (hasTrackRecord) {
      trackRecordOtherFormStore.onFieldChange('previousExperience', 'Yes');
    }
  }, [
    borrowerTrackRecordVerificationStore.properties?.length,
    borrowerTrackRecordVerificationStore.entities?.length,
  ]);

  // Depend only on the primitive loanId that gates the assignment — not on the
  // whole MobX store object (whose reference can change on re-render and would
  // re-run this effect unnecessarily).
  useEffect(() => {
    const lId = store?.documentStore?.loanStore?.loanDetails?.loanId;
    if (lId != null && `${lId}` !== '' && !borrowerBaseballCardStore.loanId) {
      borrowerBaseballCardStore.loanId = lId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.documentStore?.loanStore?.loanDetails?.loanId]);

  const handleNext = async () => {
    try {
      await borrowerTrackRecordVerificationStore.uploadGCAndBrokerLicenses();
      borrowerTrackRecordVerificationStore.validateTrackRecordTemplate();

      // Persist whatever the user entered in the active "Add Track Record" action
      // using the original save APIs. The entity/property agent is invoked earlier,
      // on the Search click — not here. The upload flow still fires the agent here
      // (there is no Search step for a template upload).
      if (inlineEntityActive && entitySaveRef.current) {
        await entitySaveRef.current();
      } else if (inlinePropertyActive && propertySaveRef.current) {
        await propertySaveRef.current();
      } else if (inlineUploadActive && inlineTemplateUploads.length > 0) {
        // Upload already saved via parseTrackRecordTemplate on drop; also fire the agent.
        // Pass loanId so the backend can resolve the uploaded Track Record doc's S3 path.
        borrowerTrackRecordVerificationStore.invokeAgentFireAndForget({
          loanId:
            store?.documentStore?.loanStore?.loanDetails?.loanId ??
            borrowerBaseballCardStore?.loanId,
          trackRecordExcel: inlineTemplateUploads[0]?.name,
        });
      }
    } catch (error) {
      // Don't run the final save flow if the entity/property save failed.
      console.error('Error saving track record data:', error);
      borrowerTrackRecordVerificationStore.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while saving track record data',
      });
      return;
    }

    borrowerTrackRecordVerificationStore.saveAndUploadGCAndBrokerData((token) =>
      onSaveCompleted ? onSaveCompleted() : store.fetchPublicFormInformation(token)
    );
  };

  const handleCardClick = async (type: string) => {
    await borrowerTrackRecordVerificationStore.uploadGCAndBrokerLicenses();
    trackRecordOtherFormStore.onFieldChange('previousExperience', 'Yes');
    if (type === 'upload') {
      // Template upload mode — same as master when "Upload using Track Record Template".
      borrowerTrackRecordVerificationStore.setDigitalTrackRecord(false);
      setInlineUploadActive(true);
      setInlineEntityActive(false);
      setInlinePropertyActive(false);
      return;
    }
    borrowerTrackRecordVerificationStore.setDigitalTrackRecord(true);
    if (type === 'entityName') {
      borrowerBaseballCardStore.setCurrentBorrower(borrowerTrackRecordVerificationStore?.borrowerInfo?.borrowerId);
      setInlineEntityActive(true);
      setInlineUploadActive(false);
      setInlinePropertyActive(false);
      return;
    }
    if (type === 'propertyAddress') {
      borrowerBaseballCardStore.setCurrentBorrower(borrowerTrackRecordVerificationStore?.borrowerInfo?.borrowerId);
      setInlinePropertyActive(true);
      setInlineUploadActive(false);
      setInlineEntityActive(false);
      return;
    }
    borrowerTrackRecordVerificationStore.trackRecordPropertyFormStore.onFieldChange('screenType', type);
    borrowerTrackRecordVerificationStore.setPropertyFormActive(true);
  };

  const handleInlineEntityClose = () => {
    setInlineEntityActive(false);
  };

  const handleInlinePropertyClose = () => {
    setInlinePropertyActive(false);
  };

  // Persist the edited Borrower Experience (internal portal). Mirrors the legacy
  // card's save: the modal mutates baseballCardDetails in place, then we send the
  // current values via saveExperienceComment and refetch to stay consistent.
  const handleExperienceSave = async () => {
    const s: any = borrowerBaseballCardStore;
    // Single source of truth for the id, with the verification store's borrowerId
    // as the canonical fallback so the save and the refetch never diverge.
    const experienceBorrowerId =
      s.currentBorrower ?? borrowerTrackRecordVerificationStore?.borrowerInfo?.borrowerId;
    try {
      const request = {
        comment: s.baseballCardDetails?.experienceComment,
        rentalExp: s.baseballCardDetails?.rentalExp,
        bridgeExp: s.baseballCardDetails?.bridgeExp,
        rentalMfExperience: s.baseballCardDetails?.rentalMfExperience,
        groundupExperience: s.baseballCardDetails?.groundupExperience,
        totalPropertiesOwned: s.baseballCardDetails?.totalPropertiesOwned,
      };
      await s.saveExperienceComment(experienceBorrowerId, request);
      s.showSuccessNotification?.('Experience saved successfully');
      if (experienceBorrowerId) {
        await borrowerBaseballCardStore.fetchBaseballCardData(experienceBorrowerId);
      }
    } catch (error) {
      s.showErrorNotification?.('Error while saving experience');
    } finally {
      setEditExperienceOpen(false);
    }
  };

  // "Save and Finish" shows once there's something to persist in the active
  // look-up grid (or an uploaded template).
  const hasLookupSelection =
    (inlineEntityActive && (borrowerBaseballCardStore.selectedPublicTrackRecords?.length > 0)) ||
    (inlinePropertyActive && (borrowerBaseballCardStore.lookUpSelectedProperties?.length > 0)) ||
    (inlineUploadActive && inlineTemplateUploads.length > 0);
  // GC / RE-agent license files are persisted only via Save
  // (saveAndUploadGCAndBrokerData), so also show Save once a license is uploaded —
  // otherwise those uploads can never be saved.
  const hasGcOrBrokerLicense =
    (borrowerTrackRecordVerificationStore.generalContractorUploads?.length > 0) ||
    (borrowerTrackRecordVerificationStore.brokerUploads?.length > 0);
  const showSaveAndFinish = hasLookupSelection || hasGcOrBrokerLicense;

  return (
    <Grid container spacing={0} style={{ width: '100%', margin: 0, marginTop: '-24px' }}>

      {/* ── BORROWER EXPERIENCE (internal portal only) ── */}
      {isInternal && (
        <Grid item xs={12} style={{ backgroundColor: '#fff', padding: '8px 24px 0' }}>
          <Box className={classes.experienceCard}>
            <Box className={classes.experienceHeader}>
              <Typography variant='subtitle1' style={{ fontWeight: 700, fontSize: 18 }}>
                Borrower Experience
              </Typography>
              {!borrowerBaseballCardStore?.baseballCardDetails?.lockedData && (
                <RocButton
                  variant='outlined'
                  color='primary'
                  size='small'
                  testId='editBorrowerExperience'
                  startIcon={<EditIcon style={{ color: theme.palette.text.secondary }} />}
                  onClick={() => setEditExperienceOpen(true)}
                >
                  Edit
                </RocButton>
              )}
            </Box>
            <Box className={classes.experienceBody}>
              <NewBorrowerExperienceData
                store={borrowerBaseballCardStore}
                isInternal={isInternal}
                isExperienceEditable={false}
              />
            </Box>
          </Box>
          <EditBorrowerExperienceModal
          dialogClasses={centeredDialogClasses}
            open={editExperienceOpen}
            handleClose={() => setEditExperienceOpen(false)}
            store={borrowerBaseballCardStore}
            handleSave={handleExperienceSave}
          />
        </Grid>
      )}

      {/* ── QUESTIONS ZONE: radio questions ── */}
      <Grid item xs={12} style={{ backgroundColor: '#fff', padding: '0 24px 8px' }}>
        <TrackRecordThirdStep store={borrowerTrackRecordVerificationStore} documentStore={store?.documentStore} />
      </Grid>

      {/* ── Add Track Record ── */}
      {trackRecordOtherFormStore.form.fields.previousExperience.value === 'Yes' && (
        <Grid item xs={12} style={{ padding: '12px 0 16px' }}>
          <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 12, paddingLeft: 20 }}>
            Add Track Record
          </Typography>

          <Box className={classes.addTrackRecordContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Paper className={inlineUploadActive ? classes.optionButtonActive : classes.optionButton} elevation={0} onClick={() => handleCardClick('upload')}>
                  <CloudUploadIcon style={{ fontSize: 22 }} />
                  <Typography variant='subtitle1' style={{ fontWeight: 600, fontSize: 15 }}>
                    Upload Track Record
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper className={inlineEntityActive ? classes.optionButtonActive : classes.optionButtonRecommended} elevation={0} onClick={() => handleCardClick('entityName')}>
                  <span className={classes.recommendedChip}>Recommended</span>
                  <BusinessIcon style={{ fontSize: 22 }} />
                  <Typography variant='subtitle1' style={{ fontWeight: 600, fontSize: 15 }}>
                    Look Up by Entity
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper className={inlinePropertyActive ? classes.optionButtonActive : classes.optionButton} elevation={0} onClick={() => handleCardClick('propertyAddress')}>
                  <LocationOnIcon style={{ fontSize: 22 }} />
                  <Typography variant='subtitle1' style={{ fontWeight: 600, fontSize: 15 }}>
                    Look Up by Property
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Inline upload area (when Upload Track Record is selected) */}
            {inlineUploadActive && (
              <Box style={{ marginTop: 16 }}>
                <CompactFileUploadArea
                  items={inlineTemplateUploads}
                  setItems={(items) => setInlineTemplateUploads(items)}
                  multiple={false}
                  documentLabel="your Track Record"
                />
              </Box>
            )}

            {/* Inline entity lookup (when Look Up by Entity is selected) */}
            {inlineEntityActive && (
              <Box style={{ marginTop: 16 }}>
                <PublicTrackRecordLineItem
                  store={borrowerBaseballCardStore}
                  isOpen={true}
                  handleClose={handleInlineEntityClose}
                  formStore={borrowerTrackRecordVerificationStore.trackRecordPropertyFormStore}
                  compactLookup
                  onSearchInvokeAgent={(entityNames) => fireAgentForEntity(entityNames)}
                  onRegisterSave={(save) => { entitySaveRef.current = save; }}
                />
              </Box>
            )}

            {/* Inline property lookup (when Look Up by Property is selected) */}
            {inlinePropertyActive && (
              <Box style={{ marginTop: 16 }}>
                <PropertyInformationFormLineItem
                  store={borrowerBaseballCardStore}
                  title='Property Details'
                  doPropertyLookup={true}
                  onClose={handleInlinePropertyClose}
                  formStore={borrowerTrackRecordVerificationStore.trackRecordPropertyFormStore}
                  compactLookup
                  onSearchInvokeAgent={(customTransactions) => fireAgentForProperty(customTransactions)}
                  onManualAddInvokeAgent={(data) => borrowerTrackRecordVerificationStore.invokeAgent({
                    manualProperty: data?.manualProperty,
                    bridge: data?.bridge,
                    groundUp: data?.groundUp,
                    term: data?.term,
                    recordSourceType: 'User Inputted',
                  })}
                  onRegisterSave={(save) => { propertySaveRef.current = save; }}
                />
              </Box>
            )}

            {/* Bottom link */}
            <Box display='flex' alignItems='center' style={{ marginTop: 14 }}>
              <InfoOutlinedIcon style={{ fontSize: 20, color: '#0288D1', marginRight: 6 }} />
              {(inlineEntityActive || inlinePropertyActive) ? (
                <>
                  <Typography variant='body2' style={{ color: '#555', marginRight: 6 }}>
                    Your property is not listed?
                  </Typography>
                  <span
                    className={classes.templateLink}
                    onClick={() => {
                      borrowerTrackRecordVerificationStore.trackRecordPropertyFormStore.onFieldChange('screenType', 'addProperty');
                      borrowerBaseballCardStore.setPropagateAddress(true);
                      borrowerTrackRecordVerificationStore.setPropertyFormActive(true);
                    }}
                  >
                    click here to add it now
                  </span>
                </>
              ) : (
                <>
                  <Typography variant='body2' style={{ color: '#555', marginRight: 6 }}>
                    Don't have a template?
                  </Typography>
                  <span className={classes.templateLink} onClick={downloadTrackRecordTemplate}>
                    <GetAppIcon style={{ fontSize: 18, marginRight: 4 }} />
                    Download template
                  </span>
                </>
              )}
            </Box>
          </Box>
        </Grid>
      )}

      {/* ── Track Record properties table (when previousExperience is Yes) ── */}
      {trackRecordOtherFormStore.form.fields.previousExperience.value === 'Yes' && (
        <Grid item xs={12} style={{ padding: '0 24px' }}>
          <TrackRecordFirstStep store={borrowerTrackRecordVerificationStore} onDocUploadCallback={onDocUploadCallback} hideTable />
        </Grid>
      )}

      {/* ── Uploaded Track Record file (downloadable) ── */}
      {borrowerTrackRecordVerificationStore.uploadedTrackRecordFile && (
        <Grid item xs={12} style={{ padding: '8px 24px 0' }}>
          <Typography variant='subtitle1' style={{ fontWeight: 700, marginBottom: 8 }}>
            Uploaded Track Record
          </Typography>
          <FileUploadItemWithDownload
            name={borrowerTrackRecordVerificationStore.uploadedTrackRecordFile.originalFileName ?? ''}
            // `file` is only used by the rename/remove flow, which is hidden when
            // disabled — so it's intentionally unset for this read-only row.
            file={undefined as any}
            taskId={borrowerTrackRecordVerificationStore.uploadedTrackRecordFile.taskId}
            originalFileName={borrowerTrackRecordVerificationStore.uploadedTrackRecordFile.originalFileName}
            fileExtension={borrowerTrackRecordVerificationStore.uploadedTrackRecordFile.fileExtension}
            disabled
            onSaveName={() => { }}
            onRemove={() => { }}
            store={borrowerTrackRecordVerificationStore}
          />
        </Grid>
      )}

      {/* ── Existing records: Properties / Entities tabs (both portals) ── */}
      <Grid item xs={12} style={{ padding: '8px 24px 0' }}>
        <Tabs
          tabs={[{ label: 'Properties' }, { label: 'Entities' }, { label: 'Licenses' }]}
          selectedTab={bottomTab}
          onTabChange={(_e, index) => setBottomTab(index)}
        />
        <Box style={{ marginTop: 16 }}>
          {bottomTab === 0 && (
            <TrackRecordProperties
              store={borrowerTrackRecordVerificationStore}
              borrowerBaseballCardStore={borrowerBaseballCardStore}
              isInternal={isInternal}
            />
          )}
          {bottomTab === 1 && (
            <TrackRecordEntities
              store={borrowerTrackRecordVerificationStore}
              borrowerBaseballCardStore={borrowerBaseballCardStore}
              isInternal={isInternal}
            />
          )}
          {bottomTab === 2 && (
            <TrackRecordLicenses
              store={borrowerTrackRecordVerificationStore}
              isInternal={isInternal}
              documentStore={store?.documentStore}
            />
          )}
        </Box>
      </Grid>

      {/* ── FOOTER ── */}
      <Grid item xs={12}>
        <Box display='flex' justifyContent='flex-end' alignItems='center' style={{ padding: '16px 24px 24px', gap: 8 }}>
          {showSaveAndFinish && (
            <RocButton
              variant='contained'
              color='primary'
              onClick={handleNext}
              testId='saveTrackRecord'
              disabled={borrowerTrackRecordVerificationStore.isUploadingLicense}
            >
              Save and Finish
            </RocButton>
          )}
        </Box>
      </Grid>

    </Grid>
  );
});
